import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MainLayout } from '../../components/MainLayout';
import { TimeEntryDialog } from './TimeEntryDialog';
import api from '../../lib/api';
import type { TimeEntry, Employee } from '../../types';
import { formatDate, formatTime, formatDuration } from '../../lib/utils';
import { cn } from '../../lib/utils';
import {
  Card, CardContent, CardHeader,
  Button, Input, Badge, Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@werkstatt/ui';
import { Plus, Pencil, Trash2, LogIn, LogOut } from 'lucide-react';

export default function TimeEntries() {
  const queryClient = useQueryClient();
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<TimeEntry | null>(null);

  const { data: employees = [] } = useQuery({
    queryKey: ['employees', true],
    queryFn: async () => {
      const res = await api.get<Employee[]>('/employees/?is_active=true');
      return res.data;
    },
  });

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['time-entries', employeeFilter, dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (employeeFilter && employeeFilter !== 'all') params.set('employee_id', employeeFilter);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const res = await api.get<TimeEntry[]>(`/time_entries/?${params}`);
      return res.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/time_entries/${id}`),
    onSuccess: () => {
      toast.success('Zeitbuchung gelöscht');
      queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });

  const clockOutMutation = useMutation({
    mutationFn: (id: string) => api.put(`/time_entries/${id}/clock-out`, { break_minutes: 0 }),
    onSuccess: () => {
      toast.success('Ausgestempelt');
      queryClient.invalidateQueries({ queryKey: ['time-entries'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: () => toast.error('Fehler beim Ausstempeln'),
  });

  const employeeMap = Object.fromEntries(employees.map((e) => [e.id, e]));
  const openEntries = entries.filter((e) => !e.clock_out);

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold">Zeitbuchungen</h1>
            <p className="text-muted-foreground mt-1">Stempelzeiten & Arbeitsstunden</p>
          </div>
          <Button onClick={() => { setEditEntry(null); setDialogOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Manuelle Buchung
          </Button>
        </div>

        {openEntries.length > 0 && (
          <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/20">
            <CardHeader className="pb-2">
              <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
                <LogIn className="inline h-4 w-4 mr-1" />
                {openEntries.length} offene Buchung{openEntries.length > 1 ? 'en' : ''} (noch nicht ausgestempelt)
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {openEntries.map((e) => {
                  const emp = employeeMap[e.employee_id];
                  return (
                    <div key={e.id} className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded px-3 py-1.5 text-sm border">
                      <span>{emp ? `${emp.first_name} ${emp.last_name}` : e.employee_id}</span>
                      <span className="text-muted-foreground">seit {formatTime(e.clock_in)}</span>
                      <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => clockOutMutation.mutate(e.id)}>
                        <LogOut className="h-3 w-3 mr-1" />
                        Ausstempeln
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <Card variant="glass">
          <CardHeader>
            <div className="flex flex-wrap gap-3 items-center">
              <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                <SelectTrigger className="w-56">
                  <SelectValue placeholder="Alle Mitarbeiter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Mitarbeiter</SelectItem>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.last_name}, {e.first_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" placeholder="Von" />
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" placeholder="Bis" />
              <Button variant="outline" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); setEmployeeFilter('all'); }}>
                Zurücksetzen
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-muted-foreground text-sm py-8 text-center">Lade...</p>
            ) : entries.length === 0 ? (
              <p className="text-muted-foreground text-sm py-8 text-center">Keine Zeitbuchungen gefunden</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-3 pr-4 font-medium">Mitarbeiter</th>
                      <th className="py-3 pr-4 font-medium">Datum</th>
                      <th className="py-3 pr-4 font-medium">Einstempeln</th>
                      <th className="py-3 pr-4 font-medium">Ausstempeln</th>
                      <th className="py-3 pr-4 font-medium">Pause</th>
                      <th className="py-3 pr-4 font-medium">Ist-Zeit</th>
                      <th className="py-3 pr-4 font-medium">Typ</th>
                      <th className="py-3 pr-4 font-medium">Notiz</th>
                      <th className="py-3 font-medium">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e) => {
                      const emp = employeeMap[e.employee_id];
                      const isOpen = !e.clock_out;
                      return (
                        <tr key={e.id} className={cn("border-b", isOpen ? "bg-amber-50 dark:bg-amber-950/20" : "hover:bg-muted/30")}>
                          <td className="py-3 pr-4 font-medium">
                            {emp ? `${emp.last_name}, ${emp.first_name}` : e.employee_id}
                          </td>
                          <td className="py-3 pr-4">{formatDate(e.date)}</td>
                          <td className="py-3 pr-4">{formatTime(e.clock_in)}</td>
                          <td className="py-3 pr-4">
                            {e.clock_out ? formatTime(e.clock_out) : (
                              <Badge variant="warning">Offen</Badge>
                            )}
                          </td>
                          <td className="py-3 pr-4">{e.break_minutes} min</td>
                          <td className="py-3 pr-4">
                            {e.work_minutes != null ? formatDuration(e.work_minutes) : '—'}
                          </td>
                          <td className="py-3 pr-4">
                            <Badge variant={e.entry_type === 'overtime' ? 'destructive' : 'default'}>
                              {e.entry_type === 'overtime' ? 'Überstunden' : 'Arbeit'}
                            </Badge>
                          </td>
                          <td className="py-3 pr-4 max-w-32 truncate text-muted-foreground">{e.note || '—'}</td>
                          <td className="py-3">
                            <div className="flex items-center gap-1">
                              {isOpen && (
                                <Button variant="ghost" size="sm" onClick={() => clockOutMutation.mutate(e.id)}>
                                  <LogOut className="h-4 w-4 text-green-600" />
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" onClick={() => { setEditEntry(e); setDialogOpen(true); }}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => {
                                if (confirm('Zeitbuchung löschen?')) deleteMutation.mutate(e.id);
                              }}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <TimeEntryDialog open={dialogOpen} onClose={() => setDialogOpen(false)} entry={editEntry} />
    </MainLayout>
  );
}
